import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { loyaltyPrograms } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { CheckCircle2, Gift, Loader2, XCircle, ArrowRight, Award } from 'lucide-react';

export default function LoyaltyCheckin() {
  const { bid } = useParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState('checking'); // checking | success | error
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!user) { navigate(`/login?redirect=/loyalty/checkin/${bid}`); return; }
    if (!bid) return;
    loyaltyPrograms.checkin(bid)
      .then(res => { setResult(res); setState('success'); refresh?.(); })
      .catch(e => { setErr(e?.response?.data?.detail || 'Check-in failed'); setState('error'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bid, user?.id]);

  if (state === 'checking') return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="pbb-card p-8 text-center max-w-md w-full">
        <Loader2 className="mx-auto animate-spin text-green-400" size={28}/>
        <div className="mt-3 font-semibold">Recording your visit…</div>
      </div>
    </div>
  );

  if (state === 'error') return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="pbb-card p-8 text-center max-w-md w-full" data-testid="checkin-error">
        <XCircle className="mx-auto text-red-400" size={30}/>
        <div className="mt-3 font-semibold">Couldn't check you in</div>
        <p className="text-sm text-gray-400 mt-1">{err}</p>
        <Link to="/loyalty" className="pbb-btn-green mt-5 inline-block max-w-xs">Back to Loyalty</Link>
      </div>
    </div>
  );

  const stamps = result?.stamps || 0;
  const total = result?.visits_required || 10;
  const pct = Math.min(100, (stamps / total) * 100);

  return (
    <div className="min-h-[60vh] flex items-center justify-center py-8">
      <div className="pbb-card p-6 text-center max-w-md w-full space-y-4" data-testid="checkin-success">
        <CheckCircle2 className="mx-auto text-green-400" size={36}/>
        <div>
          <div className="text-xl font-semibold">You got a stamp!</div>
          <div className="text-sm text-gray-400 mt-1">at <b className="text-white">{result?.businessName}</b></div>
        </div>
        <div className="rounded-lg bg-[#0d1f14] border border-[#153b23] p-4">
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>{result?.programName}</span>
            <span>{stamps}/{total}</span>
          </div>
          <div className="h-2 mt-2 rounded-full bg-[#111] overflow-hidden">
            <div className="h-full bg-green-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5 mt-3">
            {Array.from({ length: total }).map((_, i) => (
              <div key={i} className={`aspect-square rounded-md border ${i < stamps ? 'bg-green-500/25 border-green-400 text-green-300 shadow-[0_0_0_1px_rgba(74,222,128,0.35)]' : 'bg-[#1a2a20] border-dashed border-green-600/40 text-gray-500'} flex items-center justify-center text-xs font-bold`}>
                {i < stamps ? '★' : '·'}
              </div>
            ))}
          </div>
        </div>
        {result?.completed ? (
          <div className="rounded-lg bg-green-600/10 border border-green-600/40 p-4 space-y-2" data-testid="checkin-reward-ready">
            <div className="text-sm text-green-300 inline-flex items-center gap-2 justify-center">
              <Gift size={16}/> Card full — reward unlocked!
            </div>
            {result?.redemptionCode && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-green-400/80">Show this code in-store</div>
                <div
                  data-testid="checkin-reward-code"
                  className="mt-1 text-3xl font-black tracking-[0.35em] text-white bg-black/40 border border-green-500/30 rounded-lg py-3 select-all"
                >
                  {result.redemptionCode}
                </div>
                <div className="mt-2 text-xs text-gray-300">
                  Reward: <b className="text-green-300">{result.card?.reward || result.reward || 'your reward'}</b>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-gray-400 inline-flex items-center gap-1 justify-center">
            <Award size={12} className="text-green-400"/> {total - stamps} more visit{total - stamps === 1 ? '' : 's'} to unlock your reward
          </div>
        )}
        <div className="flex gap-2 justify-center pt-1">
          <Link to="/loyalty" data-testid="checkin-view-cards" className="pbb-btn-green inline-flex items-center gap-1">View my cards <ArrowRight size={14}/></Link>
          <Link to={`/business/${bid}`} className="px-3 py-2.5 rounded-lg border border-[#2a2a2a] text-sm">Back to business</Link>
        </div>
      </div>
    </div>
  );
}
